import {
  HOSTED_LIBRARIES,
  PAGE_JSON,
  PAGE_LIMITS,
  pageJson,
  relativeTo,
  resolveIn,
  resolvePageFiles,
  type PageJson,
} from './pages.js';

/**
 * `p2b page check`: everything that would break or be blocked when a page is served on the platform, each with the
 * exact fix, written so an AI can repair the page itself. Pure: the caller supplies the files (CLI: the workspace;
 * MCP: the platform's current versions).
 */

export interface PageCheckFile {
  /** Workspace path. */
  path: string;
  size: number;
  /** UTF-8 text for HTML, JS, CSS, SVG and JSON files (others may omit it). */
  text?: string;
}

export interface PageCheckInput {
  /** The page folder, a workspace path ("models/R/reports/arekstrar"). */
  dir: string;
  /** Every file under the folder (workspace paths), page.json included, with text for text files. */
  files: PageCheckFile[];
  /** Is this a file of the workspace? Used for links and for files outside the folder. */
  exists(path: string): boolean;
  /** Workspace files matching a workspace-relative glob, for `files` in page.json. */
  matchGlob(glob: string): string[];
  /** Size of a workspace file outside the folder (page files that climb out), when known. */
  sizeOf?(path: string): number | undefined;
  /** The model viewer's folder ("models/R/"), when the workspace has one: links into it open the viewer. */
  viewerRoot?: string;
}

export interface PageProblem {
  severity: 'error' | 'warning';
  code: string;
  /** Workspace path of the file, and the 1-based line. */
  file: string;
  line?: number;
  message: string;
  /** What to change, concretely. */
  fix: string;
  /** How many times the same problem occurs in the file (it is reported once). */
  count?: number;
  /** Further lines (first few) with the same problem. */
  lines?: number[];
}

export interface PageCheckResult {
  ok: boolean;
  page: PageJson | null;
  /** The files the page serves (workspace paths). */
  files: string[];
  errors: number;
  warnings: number;
  problems: PageProblem[];
  bytes: number;
}

const TEXT_EXT = /\.(html?|m?js|css|svg|json|txt|md)$/i;
const SCAN_EXT = /\.(html?|m?js|css|svg)$/i;
const NAMESPACES = new Set([
  'http://www.w3.org/2000/svg',
  'http://www.w3.org/1999/xlink',
  'http://www.w3.org/1999/xhtml',
  'http://www.w3.org/XML/1998/namespace',
  'http://www.w3.org/2000/xmlns/',
  'http://www.w3.org/1998/Math/MathML',
  'http://www.w3.org/1999/02/22-rdf-syntax-ns#',
  'http://purl.org/dc/elements/1.1/',
  'http://creativecommons.org/ns#',
  'http://www.inkscape.org/namespaces/inkscape',
  'http://sodipodi.sourceforge.net/DTD/sodipodi-0.dtd',
]);

/** A small glob matcher (`**`, `*`, `?`, `{a,b}`) for workspace paths; the CLI and server pass picomatch for the rest. */
export function globHit(glob: string, path: string): boolean {
  let re = '';
  for (let i = 0; i < glob.length; i++) {
    const c = glob[i]!;
    if (c === '*') {
      if (glob[i + 1] === '*') {
        i++;
        if (glob[i + 1] === '/') {
          i++;
          re += '(?:.*/)?';
        } else re += '.*';
      } else re += '[^/]*';
    } else if (c === '?') re += '[^/]';
    else if (c === '{') {
      const j = glob.indexOf('}', i);
      if (j < 0) re += '\\{';
      else {
        re += `(?:${glob
          .slice(i + 1, j)
          .split(',')
          .map((x) => x.replace(/[.+^$()|[\]\\]/g, '\\$&').replace(/\*/g, '[^/]*'))
          .join('|')})`;
        i = j;
      }
    } else re += c.replace(/[.+^$()|[\]\\]/g, '\\$&');
  }
  return new RegExp(`^${re}$`).test(path);
}

export function isTextPath(path: string): boolean {
  return TEXT_EXT.test(path);
}

function lineAt(starts: number[], index: number): number {
  let lo = 0;
  let hi = starts.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (starts[mid]! <= index) lo = mid;
    else hi = mid - 1;
  }
  return lo + 1;
}

function lineStarts(text: string): number[] {
  const out = [0];
  for (let i = 0; i < text.length; i++) if (text.charCodeAt(i) === 10) out.push(i + 1);
  return out;
}

const dirOf = (p: string) => (p.includes('/') ? p.slice(0, p.lastIndexOf('/') + 1) : '');
const mb = (n: number) => `${(n / 1024 / 1024).toFixed(1)} MB`;

/** A hosted equivalent of a CDN URL ("https://cdn.jsdelivr.net/npm/three@0.160.0/build/x.js" → "/lib/three@0.160.0/build/x.js"). */
export function hostedEquivalent(url: string): string | null {
  const m =
    /^https?:\/\/(?:cdn\.jsdelivr\.net\/npm|unpkg\.com|esm\.sh|cdnjs\.cloudflare\.com\/ajax\/libs)\/([^@/]+)(?:@([^/]+))?(\/[^?#]*)?/i.exec(
      url,
    );
  if (!m) return null;
  const lib = HOSTED_LIBRARIES.find((l) => l.name === m[1]!.toLowerCase());
  if (!lib) return null;
  let rest = (m[3] ?? '').replace(/^\//, '');
  if (!rest || !lib.paths.some((p) => rest.startsWith(p))) rest = lib.main;
  if (lib.name === 'echarts' && !rest.startsWith('dist/')) rest = lib.main;
  return `/lib/${lib.name}@${lib.version}/${rest}`;
}

const LIB_LIST = HOSTED_LIBRARIES.map((l) => `/lib/${l.name}@${l.version}/${l.main}`).join(', ');

/** Is `path` (absolute path on the content host) one of the hosted library files? */
function isHostedLibPath(path: string): boolean {
  const m = /^\/lib\/([^@/]+)@([^/]+)\/(.+)$/.exec(path);
  if (!m) return false;
  const lib = HOSTED_LIBRARIES.find((l) => l.name === m[1] && l.version === m[2]);
  return !!lib && lib.paths.some((p) => m[3]!.startsWith(p) || m[3] === p);
}

type RefKind = 'load' | 'link' | 'string';

interface Ref {
  url: string;
  index: number;
  kind: RefKind;
}

/** Every URL-like reference in a text file: attributes, CSS url(), imports, fetch, and string literals in scripts. */
function references(path: string, text: string): Ref[] {
  const out: Ref[] = [];
  const seen = new Set<number>();
  const add = (url: string, index: number, kind: RefKind) => {
    if (seen.has(index)) return;
    seen.add(index);
    out.push({ url, index, kind });
  };
  const html = /\.(html?|svg)$/i.test(path);
  if (html) {
    // <a href> / <area href> are links; everything else with src/href/poster/data/action loads (or submits).
    const tag = /<([a-zA-Z][\w-]*)\b([^>]*)>/g;
    for (let m: RegExpExecArray | null; (m = tag.exec(text));) {
      const name = m[1]!.toLowerCase();
      if (name === 'script' || name === 'style') {
        // attributes only; content is scanned below
      }
      const attrs = m[2]!;
      const attr = /\s(src|href|xlink:href|poster|data|action|srcset)\s*=\s*("([^"]*)"|'([^']*)')/gi;
      for (let a: RegExpExecArray | null; (a = attr.exec(attrs));) {
        const an = a[1]!.toLowerCase();
        const value = a[3] ?? a[4] ?? '';
        const at = m.index + 1 + m[1]!.length + a.index + a[0].indexOf(a[2]!) + 1;
        const isLink = (name === 'a' || name === 'area') && (an === 'href' || an === 'xlink:href');
        const isMeta =
          name === 'link' &&
          /\brel\s*=\s*["']?(canonical|alternate|author|license|help|next|prev|bookmark)/i.test(attrs);
        if (an === 'srcset') {
          for (const part of value.split(',')) {
            const u = part.trim().split(/\s+/)[0];
            if (u) add(u, at + value.indexOf(u), 'load');
          }
        } else add(value, at, isLink || isMeta ? 'link' : 'load');
      }
    }
  }
  const css = /url\(\s*(?:"([^"]*)"|'([^']*)'|([^)\s]*))\s*\)/g;
  for (let m: RegExpExecArray | null; (m = css.exec(text));) {
    const v = m[1] ?? m[2] ?? m[3] ?? '';
    add(v, m.index + m[0].indexOf(v), 'load');
  }
  const imp =
    /(?:\bimport\s*(?:[\w*{}\s,$]+from\s*)?|\bimport\s*\(\s*|\bfetch\s*\(\s*|new\s+(?:Shared)?Worker\s*\(\s*|importScripts\s*\(\s*)(["'`])([^"'`]+)\1/g;
  for (let m: RegExpExecArray | null; (m = imp.exec(text));) {
    add(m[2]!, m.index + m[0].lastIndexOf(m[2]!), 'load');
  }
  // Absolute URLs anywhere (string literals in scripts, JSON data, text).
  const abs = /\b(?:https?:)?\/\/[^\s"'`<>()\\]+|\bfile:\/\/[^\s"'`<>()\\]+/g;
  for (let m: RegExpExecArray | null; (m = abs.exec(text));) {
    if (m[0].startsWith('//') && m.index > 0 && /[\w:]/.test(text[m.index - 1]!)) continue;
    if (m[0].startsWith('//') && !/^\/\/[a-z0-9-]+(\.[a-z0-9-]+)+/i.test(m[0])) continue;
    add(m[0], m.index, 'string');
  }
  // Relative paths in script string literals ('../../viewer.html?cam=').
  if (/\.(m?js|html?)$/i.test(path)) {
    const rel = /(["'`])(\.\.?\/[^"'`\s]*)\1?/g;
    for (let m: RegExpExecArray | null; (m = rel.exec(text));) add(m[2]!, m.index + 1, 'string');
  }
  return out;
}

function isLocal(url: string): boolean {
  return (
    /^(?:https?:)?\/\/(?:127\.\d+\.\d+\.\d+|localhost|0\.0\.0\.0|\[::1\])(?::\d+)?(?:[/?#]|$)/i.test(url) ||
    /^file:/i.test(url)
  );
}

export function checkPage(input: PageCheckInput): PageCheckResult {
  const dir = input.dir.replace(/\/+$/, '');
  const root = dir === '' ? '' : `${dir}/`;
  const problems: PageProblem[] = [];
  const byPath = new Map(input.files.map((f) => [f.path, f]));
  const push = (p: PageProblem) => problems.push(p);

  // page.json
  const jsonPath = `${root}${PAGE_JSON}`;
  let page: PageJson | null = null;
  const pj = byPath.get(jsonPath);
  if (!pj) {
    push({
      severity: 'error',
      code: 'no_page_json',
      file: jsonPath,
      message: `The folder has no ${PAGE_JSON}.`,
      fix: `Create ${jsonPath}: {"p2b": "page/1", "id": "<slug>", "title": "<title>", "entry": "index.html"} (or run \`p2b page init ${dir || '.'} --title "…"\`).`,
    });
  } else {
    let raw: unknown;
    try {
      raw = JSON.parse(pj.text ?? '');
    } catch (e) {
      push({
        severity: 'error',
        code: 'page_json_syntax',
        file: jsonPath,
        message: `${PAGE_JSON} is not valid JSON (${(e as Error).message}).`,
        fix: 'Fix the JSON syntax.',
      });
    }
    if (raw !== undefined) {
      const parsed = pageJson.safeParse(raw);
      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          push({
            severity: 'error',
            code: 'page_json_schema',
            file: jsonPath,
            message: `${PAGE_JSON}: ${issue.path.join('.') || '(root)'}: ${issue.message}.`,
            fix:
              issue.path[0] === 'p2b'
                ? 'Set "p2b": "page/1".'
                : issue.path[0] === 'id'
                  ? 'Set "id" to lower-case letters, digits and hyphens (2–63 characters), unique in the project, e.g. "arekstrar".'
                  : `Correct "${String(issue.path[0] ?? '')}" in ${PAGE_JSON}.`,
          });
        }
      } else page = parsed.data;
    }
  }

  // entry and files
  const pageSet = new Set<string>();
  if (page) {
    const resolved = resolvePageFiles(root, page, (g) => {
      const hits = new Set(input.matchGlob(g));
      for (const f of input.files) if (globHit(g, f.path)) hits.add(f.path);
      return [...hits];
    });
    for (const f of resolved.files) if (byPath.has(f) || input.exists(f)) pageSet.add(f);
    for (const g of resolved.unresolved)
      push({
        severity: 'error',
        code: 'files_unmatched',
        file: jsonPath,
        message: `"files" names ${g}, which matches no file.`,
        fix: `Correct the glob (relative to ${root || 'the folder'}), push the file, or remove it from "files".`,
      });
    const entry = resolveIn(root, page.entry);
    if (!entry || !entry.startsWith(root) || !byPath.has(entry)) {
      push({
        severity: 'error',
        code: 'no_entry',
        file: jsonPath,
        message: `The entry "${page.entry}" is not a file in ${dir || 'the folder'}.`,
        fix: `Write ${root}${page.entry}, or set "entry" in ${PAGE_JSON} to the page's HTML file.`,
      });
    } else if (!/\.html?$/i.test(entry)) {
      push({
        severity: 'error',
        code: 'entry_not_html',
        file: jsonPath,
        message: `The entry ${entry} is not an HTML file.`,
        fix: 'Point "entry" at an .html file.',
      });
    } else if (byPath.get(entry)!.size > PAGE_LIMITS.entryBytes) {
      push({
        severity: 'error',
        code: 'entry_too_large',
        file: entry,
        message: `The entry is ${mb(byPath.get(entry)!.size)}; the limit is ${mb(PAGE_LIMITS.entryBytes)}.`,
        fix: 'Move embedded data (base64 images, JSON) into separate files of the page and load them with relative URLs or fetch().',
      });
    }
  }
  const sizeOf = (p: string) => byPath.get(p)?.size ?? input.sizeOf?.(p) ?? 0;
  const bytes = [...pageSet].reduce((n, p) => n + sizeOf(p), 0);

  // sizes
  for (const p of pageSet) {
    if (sizeOf(p) > PAGE_LIMITS.fileBytes)
      push({
        severity: 'error',
        code: 'file_too_large',
        file: p,
        message: `${mb(sizeOf(p))}; one file of a page may be at most ${mb(PAGE_LIMITS.fileBytes)}.`,
        fix: 'Split or thin the file, or leave it out of the page folder.',
      });
  }
  if (bytes > PAGE_LIMITS.pageBytes)
    push({
      severity: 'error',
      code: 'page_too_large',
      file: root || '.',
      message: `The page's files add up to ${mb(bytes)}; the limit is ${mb(PAGE_LIMITS.pageBytes)}.`,
      fix: 'Narrow "files" in page.json to what the page loads or offers (leave out source data, PDFs it does not link).',
    });

  const known = (p: string) => byPath.has(p) || input.exists(p);
  const viewerRoot = input.viewerRoot;

  // references in text files
  for (const f of input.files) {
    if (!pageSet.has(f.path) || !SCAN_EXT.test(f.path) || f.text === undefined) continue;
    const text = f.text;
    const starts = lineStarts(text);
    const fileDir = dirOf(f.path);
    const toRoot = fileDir.split('/').filter(Boolean).length;
    const upToRoot = '../'.repeat(toRoot) || './';
    const groups = new Map<string, PageProblem>();
    const report = (key: string, at: number, make: () => PageProblem) => {
      const line = lineAt(starts, at);
      const g = groups.get(key);
      if (g) {
        g.count = (g.count ?? 1) + 1;
        g.lines ??= [];
        if (g.lines.length < 5 && line !== g.line && !g.lines.includes(line)) g.lines.push(line);
        return;
      }
      const p = make();
      p.line = line;
      groups.set(key, p);
    };

    for (const ref of references(f.path, text)) {
      const url = ref.url.trim();
      if (!url || url.startsWith('#') || /^(data|blob|mailto|tel|javascript|about):/i.test(url)) continue;
      if (/[${}]/.test(url) && !/^(?:https?:|file:|\/\/)/i.test(url)) continue; // template pieces
      if (/['"`]\s*\+|\+\s*['"`]/.test(url)) continue; // built in script ('+esc(d.viewer)+')
      if (NAMESPACES.has(url.replace(/[#]$/, '')) || /^https?:\/\/www\.w3\.org\//i.test(url)) continue;

      if (isLocal(url)) {
        const m = /^(?:(?:https?:)?\/\/[^/]+|file:\/\/[^/]*)(\/[^?#]*)?/i.exec(url);
        const origin = m ? m[0].slice(0, m[0].length - (m[1]?.length ?? 0)) : url;
        let wsPath = (m?.[1] ?? '/').replace(/^\//, '');
        try {
          wsPath = decodeURIComponent(wsPath);
        } catch {
          /* keep it encoded */
        }
        const rel = wsPath ? relativeTo(fileDir, wsPath) + url.slice(m ? m[0].length : 0) : upToRoot;
        report(`local:${origin}`, ref.index, () => ({
          severity: 'error',
          code: 'local_url',
          file: f.path,
          message: `Link to a local server (${url.length > 90 ? `${url.slice(0, 90)}…` : url}); it does not exist on the platform.`,
          fix: `Replace "${origin}/" with "${upToRoot}" (the workspace root, relative to ${fileDir || 'the workspace root'}), e.g. "${rel}". Links into the model viewer's folder open the viewer.`,
        }));
        continue;
      }

      if (/^(?:https?:)?\/\//i.test(url)) {
        const host = /^(?:https?:)?\/\/([^/?#]+)/i.exec(url)![1]!;
        const hosted = hostedEquivalent(url);
        if (ref.kind === 'link') {
          report(`link:${host}`, ref.index, () => ({
            severity: 'warning',
            code: 'external_link',
            file: f.path,
            message: `Link to ${host}: it leaves the platform and replaces the page in its frame.`,
            fix: 'Keep it only if the reader should go there, and give the <a> target="_blank" rel="noopener".',
          }));
        } else if (ref.kind === 'load' || hosted) {
          report(`load:${hosted ?? host}`, ref.index, () => ({
            severity: 'error',
            code: 'external_load',
            file: f.path,
            message: `Loads ${url.length > 90 ? `${url.slice(0, 90)}…` : url}: pages may load nothing from other hosts (blocked by the platform).`,
            fix: hosted
              ? `Use the platform's copy: "${hosted}".`
              : `Inline it, or put the file in the page folder and use a relative URL. Hosted libraries: ${LIB_LIST}.`,
          }));
        } else {
          report(`str:${host}`, ref.index, () => ({
            severity: 'warning',
            code: 'external_url',
            file: f.path,
            message: `URL to ${host} in the page (${url.length > 70 ? `${url.slice(0, 70)}…` : url}).`,
            fix: 'If the page loads or fetches it, it will be blocked: inline it or ship it in the page folder. A plain link is fine with target="_blank".',
          }));
        }
        continue;
      }

      const clean = url.split(/[?#]/)[0]!;
      if (clean === '') continue;
      if (clean.startsWith('/')) {
        if (isHostedLibPath(clean)) continue;
        if (/^\/lib\//.test(clean)) {
          report(`lib:${clean}`, ref.index, () => ({
            severity: 'error',
            code: 'unknown_library',
            file: f.path,
            message: `${clean} is not a hosted library file.`,
            fix: `Hosted libraries: ${LIB_LIST}.`,
          }));
          continue;
        }
        report(`abs:${clean}`, ref.index, () => ({
          severity: 'error',
          code: 'absolute_path',
          file: f.path,
          message: `Absolute path ${clean}: on the platform it points outside the publication.`,
          fix: `Use a path relative to ${fileDir || 'the workspace root'}${known(clean.slice(1)) ? `: "${relativeTo(fileDir, clean.slice(1))}"` : ''}.`,
        }));
        continue;
      }
      if (/^[a-z][a-z0-9+.-]*:/i.test(clean)) continue;
      let decoded = clean;
      try {
        decoded = decodeURIComponent(clean);
      } catch {
        /* keep */
      }
      const target = resolveIn(fileDir, decoded);
      if (target === null) {
        report(`above:${clean}`, ref.index, () => ({
          severity: 'error',
          code: 'outside_workspace',
          file: f.path,
          message: `${clean} climbs above the workspace root.`,
          fix: 'Remove a "../".',
        }));
        continue;
      }
      if (target === '' || target.endsWith('/')) continue;
      const inViewer = !!viewerRoot && target.startsWith(viewerRoot) && !target.startsWith(root);
      if (pageSet.has(target)) continue;
      if (inViewer && (ref.kind !== 'load' || /\.html?$/i.test(target))) {
        if (!input.exists(target))
          report(`viewer:${target}`, ref.index, () => ({
            severity: 'error',
            code: 'broken_link',
            file: f.path,
            message: `${clean} → ${target} is not a file of the workspace.`,
            fix: `Point it at the viewer page, e.g. "${relativeTo(fileDir, `${viewerRoot}viewer.html`)}".`,
          }));
        continue;
      }
      if (ref.kind === 'string' && !/\.[a-z0-9]{1,5}$/i.test(target)) continue;
      if (byPath.has(target) || input.exists(target)) {
        report(`outside:${target}`, ref.index, () => ({
          severity: ref.kind === 'link' ? 'warning' : 'error',
          code: 'not_in_page',
          file: f.path,
          message: `${clean} → ${target} is not one of the page's files, so the platform will not serve it.`,
          fix: `Add "${relativeTo(root, target)}" to "files" in ${PAGE_JSON}${/\.html?$/i.test(target) ? ', or, if it is another page, give that folder its own page.json and publish both' : ''}.`,
        }));
        continue;
      }
      report(`broken:${target}`, ref.index, () => ({
        severity: 'error',
        code: 'broken_link',
        file: f.path,
        message: `${clean} → ${target} does not exist.`,
        fix: 'Correct the path (relative to the file that uses it), or add the file to the page folder.',
      }));
    }

    const pattern = (re: RegExp, key: string, make: () => PageProblem) => {
      re.lastIndex = 0;
      for (let m: RegExpExecArray | null; (m = re.exec(text));) report(key, m.index, make);
    };
    pattern(/\b(?:localStorage|sessionStorage|indexedDB)\b|document\.cookie/g, 'storage', () => ({
      severity: 'warning',
      code: 'storage',
      file: f.path,
      message:
        'Browser storage (localStorage, sessionStorage, indexedDB, cookies) throws in the platform’s sandbox.',
      fix: 'Wrap every use in try/catch and keep working without it (state in memory or in the URL hash).',
    }));
    pattern(/\b(?:XMLHttpRequest|WebSocket|EventSource|navigator\.sendBeacon)\b/g, 'net', () => ({
      severity: 'warning',
      code: 'network',
      file: f.path,
      message: 'Network APIs other than fetch of the page’s own files are blocked.',
      fix: 'Read files of the publication with relative fetch() or p2b.read(path); nothing else is reachable.',
    }));
    pattern(/\beval\s*\(|\bnew\s+Function\s*\(/g, 'eval', () => ({
      severity: 'warning',
      code: 'eval',
      file: f.path,
      message: 'eval / new Function are blocked by the platform’s content policy.',
      fix: 'Remove them (hosted libraries do not need them).',
    }));
    if (/\.(html?|svg)$/i.test(f.path)) {
      pattern(/<base\b/gi, 'base', () => ({
        severity: 'error',
        code: 'base_tag',
        file: f.path,
        message: '<base> is not allowed: it would break every relative link on the platform.',
        fix: 'Remove the <base> element.',
      }));
      pattern(/<form\b[^>]*\baction\s*=/gi, 'form', () => ({
        severity: 'warning',
        code: 'form_action',
        file: f.path,
        message: 'Form submission is blocked.',
        fix: 'Handle the form in script (preventDefault) instead of submitting it.',
      }));
      pattern(/<(?:iframe|frame|object|embed)\b/gi, 'frame', () => ({
        severity: 'error',
        code: 'frame',
        file: f.path,
        message: 'Frames and embedded objects are blocked.',
        fix: 'Link to the other page instead (relative <a href>), or p2b.openPage(id).',
      }));
    }
    pattern(/target\s*=\s*["']_(?:top|parent)["']|\b(?:window\.)?(?:top|parent)\.location\b/g, 'top', () => ({
      severity: 'warning',
      code: 'top_navigation',
      file: f.path,
      message: 'The page cannot navigate the platform window.',
      fix: 'Use a plain relative link (it opens in the page frame), target="_blank", or p2b.openPage / p2b.openViewer.',
    }));
    problems.push(...groups.values());
  }

  problems.sort(
    (a, b) =>
      (a.severity === b.severity ? 0 : a.severity === 'error' ? -1 : 1) ||
      a.file.localeCompare(b.file) ||
      (a.line ?? 0) - (b.line ?? 0),
  );
  const errors = problems.filter((p) => p.severity === 'error').length;
  return {
    ok: errors === 0,
    page,
    files: [...pageSet].sort(),
    errors,
    warnings: problems.length - errors,
    problems,
    bytes,
  };
}

/** One line per problem, for terminals and MCP text. */
export function formatPageProblems(result: PageCheckResult): string[] {
  const out: string[] = [];
  for (const p of result.problems) {
    const where = `${p.file}${p.line ? `:${p.line}` : ''}`;
    const more =
      p.count && p.count > 1
        ? ` (${p.count}×${p.lines?.length ? `, also lines ${p.lines.join(', ')}${p.count - 1 > p.lines.length ? ', …' : ''}` : ''})`
        : '';
    out.push(`${p.severity === 'error' ? 'ERROR' : 'WARN '} ${where} [${p.code}] ${p.message}${more}`);
    out.push(`      fix: ${p.fix}`);
  }
  out.push(
    `${result.ok ? 'OK' : 'FAIL'}: ${result.errors} error(s), ${result.warnings} warning(s), ${mb(result.bytes)}`,
  );
  return out;
}
